// Cave84 — Generation Queue service (Phase 2 Week 7)
// Shared queue for wine_analysis, market_price_refresh, label_ocr,
// vintage_chart_year jobs. Written by the UI; consumed by the CLI batch.

import {
  getDocs,
  orderBy,
  query,
  setDoc,
  doc,
  collection,
  serverTimestamp,
  where
} from 'firebase/firestore';
import type { GenerationEntityType, GenerationQueueEntry } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';

const COLLECTION = 'generation_queue';

function col() {
  return typedCollection<GenerationQueueEntry>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export async function createQueueEntry(
  entityType: GenerationEntityType,
  entityId: string,
  priority: GenerationQueueEntry['priority'] = 'normal'
): Promise<string> {
  const ref = doc(collection(db, COLLECTION));
  const payload: Omit<GenerationQueueEntry, 'id'> = {
    entity_type: entityType,
    entity_id: entityId,
    priority,
    status: 'pending',
    requested_at: serverTimestamp() as GenerationQueueEntry['requested_at'],
  };
  await setDoc(ref, payload);
  return ref.id;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listPendingEntries(
  entityType?: GenerationEntityType
): Promise<GenerationQueueEntry[]> {
  const constraints = [
    where('status', 'in', ['pending', 'processing']),
    orderBy('requested_at', 'asc'),
  ];
  if (entityType) {
    constraints.unshift(where('entity_type', '==', entityType));
  }
  const snap = await getDocs(query(col(), ...constraints));
  return snap.docs.map(d => d.data());
}

export async function listRecentCompleted(limit = 10): Promise<GenerationQueueEntry[]> {
  const snap = await getDocs(
    query(
      col(),
      where('status', 'in', ['completed', 'failed', 'needs_owner_review']),
      orderBy('completed_at', 'desc')
    )
  );
  return snap.docs.slice(0, limit).map(d => d.data());
}

/**
 * All entries for a given entity type across ALL statuses,
 * ordered newest-first. Used by the Price Refresh Wizard progress view
 * so progress remains visible after the CLI completes.
 */
export async function listAllEntriesByType(
  entityType: GenerationEntityType,
  maxItems = 2000,
): Promise<GenerationQueueEntry[]> {
  const snap = await getDocs(
    query(
      col(),
      where('entity_type', '==', entityType),
      orderBy('requested_at', 'desc'),
    )
  );
  return snap.docs.slice(0, maxItems).map(d => d.data());
}
